import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, User, FolderOpen, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { articleApi } from '@/user/api';
import type { ArticleVO } from '@/types/api-entity';
// 引入 Quill 核心样式，确保内容页渲染时类名生效
import 'quill/dist/quill.core.css';
import './article.css';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<ArticleVO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadArticle = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const result = await articleApi.getArticleDetail(parseInt(id));
      if (result.code === 200) {
        setArticle(result.data);
      } else {
        setError(result.message || '文章不存在');
      }
    } catch (err) {
      setError('加载文章失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-CN');
  };

  useEffect(() => {
    loadArticle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-20 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-[#1c5bff] rounded-full animate-spin" />
            <p className="mt-4 text-gray-500">加载中...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-20 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">{error || '文章不存在'}</h2>
            <Link to="/articles">
              <Button className="mt-4 bg-[#1c5bff] text-white hover:bg-[#1547cc]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                返回文章列表
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20 bg-gray-50">
        <div className="section-container py-12">
          <Link to="/articles">
            <Button variant="outline" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回列表
            </Button>
          </Link>

          <article className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {article.coverImage && (
              <div className="aspect-[21/9] overflow-hidden bg-gray-100">
                <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="p-8 md:p-12">
              {article.category && (
                <div className="flex items-center gap-2 mb-4">
                  <FolderOpen className="w-4 h-4 text-[#1c5bff]" />
                  <span className="text-[#1c5bff] font-medium">{article.category}</span>
                </div>
              )}

              <h1 className="text-3xl md:text-4xl font-bold text-black mb-6">{article.title}</h1>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-100">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {article.authorName || '匿名'}
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {article.viewCount} 次浏览
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.publishTime)}
                </span>
              </div>

              {article.summary && (
                <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                  <p className="text-gray-600 italic">{article.summary}</p>
                </div>
              )}

              {/* 关键修改：添加 ql-editor 类名 + 自定义 article-content 样式 */}
              <div 
                className="mt-8 article-content ql-editor"
                dangerouslySetInnerHTML={{ __html: article.content || '' }}
              />
              <button
                  onClick={() => window.location.href = '/pingan'}
                  className="mt-6 px-6 py-3 rounded-lg text-white font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  style={{ backgroundColor: '#FF6B3D' }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#E55A2B'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#FF6B3D'; }}
                >
                  立即查看
                </button>

              {article.tags && (
                <div className="mt-12 pt-8 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500 text-sm">标签：</span>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.split(',').map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}
